import type { Edge, Node } from '@xyflow/react'
import { keyBy, omit, partition } from 'lodash'
import { v4 } from 'uuid'
import { generateWhatsAppPayload } from 'v2/pages/conversations/outbound/utils'
import { getTemplate } from 'v2/pages/conversations/utils/transformers'
import { FlowSourceTypes, type FlowTypes } from 'v2/types'
import type { RBusinessHours } from 'v2/types/api/settings'
import { logger } from 'v2/utils/logger'
import type { ZodCustomIssue } from 'zod'
import type { EventCallback } from '../components/event-callbacks'
import { getNewFlowData } from '../config/base.config'
import type { ContactScreeningConfig } from '../config/contact-screening.config'
import { defaultNodeData, initialBody, MenuNodeID, promptData } from '../config/nodes.config'
import {
  buildScreeningWireIdMap,
  collapseOutboundScreeningPairs,
  expandOutboundScreeningComposites,
} from '../config/outbound-screening.config'
import { SlackChannelTypes } from '../config/trigger-node.config'
import { CONFIRM_KEYS } from '../config/validation.config'
import { DEFAULT_INITIAL_WAIT_TIME, NO_REPLY_HANDLE } from '../constants'
import { isTextAgentChannel } from '../run-info/types'
import type { AgentTasksWire } from '../sidebar/ai-agents/agent-tasks-config'
import { useFlowStore } from '../store'
import {
  type AgentNodeNodeData,
  type AgentPresetConversationNodeData,
  AgentState,
  type AiActionNodeData,
  type AiAgentChatNodeData,
  type AiAgentMessageNodeData,
  type AiAgentV2NodeData,
  type AiAgentWhatsappNodeData,
  type BranchV2NodeData,
  type BusinessHourNodeData,
  type CreateTaskNodeData,
  type DataItem,
  type FlowData,
  FlowNodeTypes,
  type GenericNode,
  type HttpRequestNodeData,
  type InitiateCallNodeData,
  type NodeDataBase,
  type NoteNodeData,
  type OutputState,
  type QueueAndRouteNodeData,
  type RFlowPayload,
  type ROutputNode,
  type SendChatNodeData,
  type SendWhatsappNodeData,
  type SystemPrompt,
  type VoicemailNodeData,
} from '../types'
import { getNodeVarsForHandle } from '.'
import { transformAiActionConfig } from './ai-action-utils'
import {
  extractTemplateVariables,
  formatAiAgentChatData,
  formatAiAgentWhatsappData,
  transformAiAgentChatConfig,
  transformAiAgentWhatsappConfig,
} from './ai-agent-utils'
import { isAiAgentV2Type } from './ai-agent-v29'
import { eventsArrayToDict, eventsDictToArray } from './event-callbacks-utils'
import {
  handleBackendValidationErrors,
  MultipleNodeValidationError,
  NodeValidationError,
} from './flow-validation'
import { processValidationErrors } from './validation-error-processor'
import { dedupeScreeningVoicemailEdges } from './voicemail-graph'

const AUTO_CREATED_SMS_NO_REPLY_CONNECTION_TYPE = 'output:auto-created-sms-no-reply-target'

// FE-BE transformation
/**
 * Transforms node data to match backend API expectations.
 * Each node type requires specific configuration to maintain compatibility with legacy APIs.
 *
 * Note: This complex transformation logic exists due to legacy API constraints.
 * With newer APIs, this could be simplified to a more generic implementation
 * that directly maps the frontend data model to the backend.
 *
 * Current transformations include:
 * - HTTP Request: Formats auth params and request body
 * - Start Node: Adds trigger configuration based on flow type
 * - Voicemail: Extracts prompt data
 * - Business Hours: Maps business hour IDs
 * - Create Task: Ensures prompt data exists
 *
 * @param node The node to transform
 * @param flowType The type of flow (voice, sms etc)
 * @returns Transformed node config matching BE expectations
 */

// Strip frontend-only fields (is_expanded / name / action_field_types) and the
// fields irrelevant to each action_type, leaving the backend-expected action
// shape. Shared by the call + chat ai_agent_v2 paths and agent_node — chat's
// in-node actions mirror voice: the agent builds them as tools via build_tools.
// biome-ignore lint/suspicious/noExplicitAny: stripping frontend-only fields
const mapAiAgentActions = (actions: any[]): any[] =>
  // biome-ignore lint/suspicious/noExplicitAny: stripping frontend-only fields
  actions.map(({ is_expanded, name, action_field_types, ...clean }: any) => {
    if (clean.action_type === 'EXECUTE_ACTION') {
      const {
        code_name: _cn,
        code_description: _cd,
        code_body: _cb,
        code_function_schema: _cfs,
        ...rest
      } = clean
      return rest
    }
    if (clean.action_type === 'CUSTOM_CODE') {
      const {
        org_app_uuid: _oau,
        action_name: _an,
        action_instructions: _ai,
        action_schema: _as,
        action_inputs: _ain,
        app_provider: _ap,
        ...rest
      } = clean
      return rest
    }
    return clean
  })

const getNodeConfig = (
  node: Node<NodeDataBase<unknown>> | Node<HttpRequestNodeData>,
  flowType: FlowTypes,
) => {
  switch (node.type) {
    case FlowNodeTypes.http_request: {
      const httpNode = node as Node<HttpRequestNodeData>
      const authParams = JSON.stringify(httpNode.data.config.http_request.auth_params)
      let body = httpNode.data.config.http_request.body
        ? JSON.parse(httpNode.data.config.http_request.body)[
            httpNode.data.config.http_request.body_type
          ]
        : '{}'
      body = typeof body !== 'string' ? JSON.stringify(body) : body
      return {
        ...httpNode.data.config.http_request,
        auth_params: authParams,
        body,
      }
    }
    case FlowNodeTypes.agent_preset_conversation: {
      const presetConfig = node.data.config as AgentPresetConversationNodeData['config']
      const { update_similar_nodes: _, ...presetNodeConfig } = presetConfig

      // If channel is whatsapp, use the standardized WhatsApp transformation utility
      if (presetNodeConfig.channel === 'whatsapp') {
        return transformAiAgentWhatsappConfig(presetNodeConfig, node.id, node.data.config.name)
      }

      // If channel is chat, use the standardized Chat transformation utility
      if (presetNodeConfig.channel === 'chat') {
        return transformAiAgentChatConfig(presetNodeConfig)
      }

      // If channel is message, convert node-level event callbacks (array -> dict)
      // like send_message; omit when none configured so BE schema defaults apply.
      if (presetNodeConfig.channel === 'message') {
        const { events, ...rest } = presetNodeConfig as Record<string, unknown>
        const dict = eventsArrayToDict(events as EventCallback[] | undefined)
        return Object.keys(dict).length ? { ...rest, events: dict } : rest
      }

      // For any other channels, return config as-is
      return presetNodeConfig
    }
    case FlowNodeTypes.start: {
      // Preserve existing triggers (set by BE for migrated/legacy flows and by
      // the trigger checkbox UI). Overwriting unconditionally with
      // [FlowSourceTypes[flowType]] serializes triggers as [null] for omni
      // flows because FlowSourceTypes has no 'omni' key.
      const existingTriggers = (node.data.config as { triggers?: unknown }).triggers
      const hasValidExisting =
        Array.isArray(existingTriggers) &&
        existingTriggers.length > 0 &&
        existingTriggers.every((t) => typeof t === 'string' && t)
      const fallback = FlowSourceTypes[flowType as FlowTypes]
      const triggers = hasValidExisting
        ? (existingTriggers as string[])
        : fallback
          ? [fallback]
          : []
      // Node-level event callbacks round-trip as a dict on the BE (legacy PHLO
      // shape: { hangup: { url, method } }); the builder edits them as an array.
      // Only attach events when a callback is actually configured — sending an
      // empty {} suppresses phlo-core's {hangup:{url:'',method:'POST'}} schema
      // default (Cerberus only defaults absent fields). SER-4772.
      const { events, ...restConfig } = node.data.config as Record<string, unknown>
      const startEvents = eventsArrayToDict(events as EventCallback[] | undefined)
      const startConfig: Record<string, unknown> = { ...restConfig, triggers }
      if (Object.keys(startEvents).length) startConfig.events = startEvents
      if ('payload_format' in node.data.config) {
        startConfig.payload_format =
          typeof node.data.config.payload_format === 'string'
            ? JSON.parse(node.data.config.payload_format)
            : node.data.config.payload_format || {}
      }
      return startConfig
    }
    case FlowNodeTypes.voicemail: {
      const voicemailNode = node as Node<VoicemailNodeData>
      const { prompt, events, ...rest } = voicemailNode.data.config as Record<string, unknown>
      const voicemailPrompt = prompt as Record<string, unknown> | undefined
      // Node-level event callbacks round-trip as a dict on the BE (voicemail
      // schema: { record: { url, method } }); the builder edits them as an array.
      // Only attach events when a callback is actually configured — sending an
      // empty {} (or a list) suppresses/violates phlo-core's dict schema, which
      // otherwise defaults an absent key ("Events: must be of dict type"). SER-5972.
      const voicemailEvents = eventsArrayToDict(events as EventCallback[] | undefined)
      const config: Record<string, unknown> = { ...rest, ...voicemailPrompt }
      if (Object.keys(voicemailEvents).length) config.events = voicemailEvents
      return config
    }
    case FlowNodeTypes.business_hour: {
      const businessHourNode = node as Node<BusinessHourNodeData>
      const { business_hrs, ...rest } = businessHourNode.data.config

      const result = {
        ...rest,
        business_hrs: business_hrs.map((hour: RBusinessHours) => hour.uuid || hour.id),
      }
      if (result.output_states) {
        delete result.output_states
      }
      return result
    }
    case FlowNodeTypes.create_task: {
      const createTaskNode = node as Node<CreateTaskNodeData>
      if (!createTaskNode.data.config.prompt.text.trim()) {
        // If the prompt text is empty, delete the prompt config
        createTaskNode.data.config.prompt.prompt_speak_type = ''
        createTaskNode.data.config.prompt.prompt_type = ''
        return { ...node.data.config }
      }
      return { ...node.data.config }
    }
    case FlowNodeTypes.send_chat: {
      const sendChatNode = node as Node<SendChatNodeData>
      if (sendChatNode.data.config.chat_content_type === 'text')
        delete sendChatNode.data.config.chat_media
      else delete sendChatNode.data.config.text
      return { ...sendChatNode.data.config }
    }
    case FlowNodeTypes.ai_agent_chat: {
      const aiAgentChatNode = node as Node<AiAgentChatNodeData>
      return transformAiAgentChatConfig(aiAgentChatNode.data.config)
    }
    case FlowNodeTypes.send_whatsapp: {
      const sendWhatsappNode = node as Node<SendWhatsappNodeData>
      if (sendWhatsappNode.data.config.whatsapp_msg_type === 'template') {
        // Validate template structure before calling generateWhatsAppPayload
        const { template: nodeTemplate } = sendWhatsappNode.data.config
        if (!nodeTemplate || !nodeTemplate.data || !nodeTemplate.data.language) {
          throw new NodeValidationError(
            'Template is required when WhatsApp message type is template. Please select a template.',
            node.id,
            node.data.config.name,
            ['template'],
          )
        }

        const payload = generateWhatsAppPayload({
          template: sendWhatsappNode.data.config.template,
          variables: sendWhatsappNode.data.config.variables || { header: [], body: [] },
          from: sendWhatsappNode.data.config.from,
          to: sendWhatsappNode.data.config.to,
        })
        const { variables: _, template: __, ...rest } = sendWhatsappNode.data.config
        return {
          ...rest,
          whatsapp_template: payload.template_attributes,
        }
      }
      return { ...sendWhatsappNode.data.config }
    }
    case FlowNodeTypes.ai_agent_whatsapp: {
      const aiAgentWhatsappNode = node as Node<AiAgentWhatsappNodeData>
      return transformAiAgentWhatsappConfig(
        aiAgentWhatsappNode.data.config,
        node.id,
        node.data.config.name,
      )
    }
    case FlowNodeTypes.ai_agent_message: {
      const aiAgentMessageNode = node as Node<AiAgentMessageNodeData>
      if (aiAgentMessageNode.data.config.conversation_type === 'ai_generated') {
        return omit(aiAgentMessageNode.data.config, ['to', 'from'])
      }
      return { ...aiAgentMessageNode.data.config }
    }
    case FlowNodeTypes.branch_v2: {
      const branchV2Node = node as Node<BranchV2NodeData>
      const conditions = branchV2Node.data.config?.conditions?.map((condition) => ({
        ...condition,
        expression_operators: Array(condition.expressions.length - 1).fill(
          condition.expression_operators[0],
        ),
      }))
      return { ...branchV2Node.data.config, conditions }
    }
    case FlowNodeTypes.ai_action: {
      const aiActionNode = node as Node<AiActionNodeData>
      return transformAiActionConfig(aiActionNode.data.config)
    }
    case FlowNodeTypes.stream: {
      const config = { ...node.data.config } as Record<string, unknown>
      // Split combined "provider:model" values into separate provider and model fields
      const sttConfig = config.stt_config as Record<string, unknown> | undefined
      if (
        sttConfig?.provider &&
        typeof sttConfig.provider === 'string' &&
        sttConfig.provider.includes(':')
      ) {
        const [provider, model] = (sttConfig.provider as string).split(':')
        config.stt_config = { ...sttConfig, provider, model }
      }
      const ttsConfig = config.tts_config as Record<string, unknown> | undefined
      if (
        ttsConfig?.provider &&
        typeof ttsConfig.provider === 'string' &&
        ttsConfig.provider.includes(':')
      ) {
        const [provider, model] = (ttsConfig.provider as string).split(':')
        config.tts_config = { ...ttsConfig, provider, model }
      }
      return config
    }
    // Real wire component (SER-5741): lean config, channel locked to call.
    // The runtime dispatches on the screening_task phlo-core emits from this.
    case FlowNodeTypes.contact_screening: {
      const config = node.data.config as unknown as ContactScreeningConfig
      return {
        channel: 'call',
        context: config.context ?? '',
        ask_permission: config.ask_permission ?? true,
        initial_wait_time: config.initial_wait_time ?? DEFAULT_INITIAL_WAIT_TIME,
      }
    }
    case FlowNodeTypes.agent_node: {
      const config = node.data.config as AgentNodeNodeData['config']
      const { agent_tasks, ...rest } = config
      // FE state mirrors the wire; strip default-false flags and empty params so
      // the saved model stays minimal.
      const wire = agent_tasks
        ? {
            variables: (agent_tasks.variables ?? []).map((row) => ({
              name: row.name,
              instructions: row.instructions,
              type: row.type,
              ...(row.params && Object.keys(row.params).length ? { params: row.params } : {}),
              ...(row.optional ? { optional: true } : {}),
              ...(row.known_value !== undefined ? { known_value: row.known_value } : {}),
              ...(row.known_value !== undefined
                ? { confirm_if_known: !!row.confirm_if_known }
                : {}),
            })),
            extract_only: (agent_tasks.extract_only ?? []).map((row) => ({
              name: row.name,
              instructions: row.instructions,
            })),
            ...(agent_tasks.confirm_announcement
              ? { confirm_announcement: agent_tasks.confirm_announcement }
              : {}),
            ...(agent_tasks.max_restarts != null ? { max_restarts: agent_tasks.max_restarts } : {}),
          }
        : agent_tasks
      return {
        ...rest,
        actions: rest.actions?.length ? mapAiAgentActions(rest.actions) : rest.actions,
        agent_tasks: wire,
      }
    }
    case FlowNodeTypes.ai_agent_v29:
    case FlowNodeTypes.ai_agent_v2: {
      const config = node.data.config as AiAgentV2NodeData['config']
      const { update_similar_nodes: _, ...configData } = config
      // Skip wait_for_response and reply_timeout for call channel (these fields are not applicable)
      if (configData.channel === 'call') {
        const { wait_for_response: __, reply_timeout: ___, ...callConfigData } = configData
        // Clean frontend-only fields and strip irrelevant fields based on action_type
        if (callConfigData.actions?.length) {
          callConfigData.actions = mapAiAgentActions(callConfigData.actions)
        }
        return callConfigData
      }
      // Text agents keep their tools and reply timeout settings.
      if (isTextAgentChannel(configData.channel)) {
        const keptConfigData = { ...configData }
        if (keptConfigData.actions?.length) {
          keptConfigData.actions = mapAiAgentActions(keptConfigData.actions)
        }
        return keptConfigData
      }
      // Unsupported channels cannot execute authored tools.
      const { actions: ____, ...nonCallConfig } = configData
      return nonCallConfig
    }
    case FlowNodeTypes.initiate_call: {
      const config = node.data.config as InitiateCallNodeData['config']
      const { events, ...rest } = config as Record<string, unknown>
      const result: Record<string, unknown> = { ...rest }
      // Only attach events when a callback is configured — sending an empty {}
      // suppresses the BE schema event defaults (Cerberus defaults absent fields
      // only). SER-4772.
      const callEvents = eventsArrayToDict(events as EventCallback[] | undefined)
      if (Object.keys(callEvents).length) result.events = callEvents
      if (result.max_call_duration && typeof result.max_call_duration === 'number') {
        // Convert max_call_duration from minutes (frontend) to seconds (backend)
        result.max_call_duration = (result.max_call_duration as number) * 60
      }
      return result
    }
    case FlowNodeTypes.queue_and_route: {
      const config = node.data.config as QueueAndRouteNodeData['config']
      const {
        update_similar_nodes: _,
        routing_variables,
        routing_agents,
        ...restConfig
      } = config as Record<string, unknown>
      const routingVariables = (routing_variables as string[]) || []
      const routingAgents = (routing_agents as string[]) || []
      const configData: Record<string, unknown> = {
        ...restConfig,
        routing_agents: [...routingAgents, ...routingVariables],
      }
      return configData
    }
    // Pre-existing nodes (not part of SER-4772) — keep their original behavior
    // of always serializing events as a dict.
    case FlowNodeTypes.multi_party_call_v2:
    case FlowNodeTypes.conference_bridge: {
      const config = node.data.config as Record<string, unknown>
      const { events, record, record_mode, record_condition, recording_option, ...rest } = config
      // The BE schema wants a flat boolean `record` plus `record_value`
      // (dynamic recording = record:false + record_value:'{{var}}', rendered at
      // runtime by get_call_record_value) and the recording option under
      // `recording`. `record` can still be phlo-core's nested GET shape
      // { record, record_value } (AS_DICTIONARY rendering group) — echoing it
      // back fails Cerberus with "Record must be of boolean type". SER-5919.
      const recordEnabled = isPlainObject(record)
        ? record.record === true ||
          Boolean(typeof record.record_value === 'string' && record.record_value.trim())
        : record === true
      const condition = typeof record_condition === 'string' ? record_condition.trim() : ''
      const isDynamic = recordEnabled && record_mode === 'dynamic' && condition !== ''
      const result: Record<string, unknown> = {
        ...rest,
        record: isDynamic ? false : recordEnabled,
        record_value: isDynamic ? condition : '',
        events: eventsArrayToDict(events as EventCallback[] | undefined),
      }
      if (
        node.type === FlowNodeTypes.multi_party_call_v2 &&
        typeof recording_option === 'string' &&
        recording_option
      ) {
        result.recording = recording_option
      }
      // The BE speak_or_play rule rejects a play prompt whose url has no file
      // name ("Please enter file name"). HoldMusicField only learns the file
      // name on upload, so defaults/manual URLs (and nodes saved before the
      // default carried one) arrive with file:'' — backfill from the url. A
      // pure node-var url is exempt on the BE, leave it alone. SER-5919.
      for (const key of ['customer_hold_music', 'agent_hold_music']) {
        const music = result[key]
        if (!isPlainObject(music)) continue
        const url = typeof music.url === 'string' ? music.url.trim() : ''
        if (!url || music.file || /^\{\{.*\}\}$/.test(url)) continue
        const basename = url.split('/').pop()?.split('?')[0]
        result[key] = { ...music, file: basename || 'music.mp3' }
      }
      // Omit an untouched voicemail drop message (speak prompt, no text, no
      // audio) — sending it with text:'' fails speak_or_play ("Please enter
      // text") AND suppresses the BE's own default text, since Cerberus only
      // defaults absent keys (same rule as events/SER-4772). SER-5919.
      const voicemail = result.voicemail_message
      if (
        isPlainObject(voicemail) &&
        (voicemail.prompt_type ?? 'speak') === 'speak' &&
        !(typeof voicemail.text === 'string' && voicemail.text.trim()) &&
        !voicemail.url &&
        !voicemail.file
      ) {
        delete result.voicemail_message
      }
      return result
    }
    case FlowNodeTypes.send_message:
    case FlowNodeTypes.get_input:
    case FlowNodeTypes.ivr_menu:
    case FlowNodeTypes.call_forward: {
      const config = node.data.config as Record<string, unknown>
      const { events, ...rest } = config
      // Drop a null/undefined call_forward `record` so non-legacy flows fall
      // back to the CoreDB org recording policy (only legacy flows surface and
      // keep an explicit record). No-op for the other node types (no record).
      if (rest.record == null) delete rest.record
      // Screening with no prompt text renders a silent <Response/> and the leg
      // drops as no-confirm, so back-fill a spoken prompt. A previously
      // back-filled sentence is regenerated so it always names the current key;
      // custom text is left alone (SER-6328).
      if (node.type === FlowNodeTypes.call_forward && rest.confirm_sound === true) {
        const prompt = (rest.confirm_prompt ?? {}) as { text?: string | null }
        if (!prompt.text?.trim() || isGeneratedConfirmPrompt(prompt.text))
          rest.confirm_prompt = { ...prompt, text: confirmPromptFor(rest.confirm_key) }
      }
      // Only attach events when a callback is configured — sending an empty {}
      // suppresses the BE schema event defaults (Cerberus defaults absent fields
      // only). SER-4772.
      const dict = eventsArrayToDict(events as EventCallback[] | undefined)
      return Object.keys(dict).length ? { ...rest, events: dict } : rest
    }
    default: {
      const config = node.data.config
      if ('update_similar_nodes' in config) {
        const { update_similar_nodes: _, ...configData } = config as Record<string, unknown>
        return configData
      }
      return config
    }
  }
}

// Data transform functions
// Prolly should be moved to a separate file (data-transformers.ts)
// FE Reactflow -> BE Server transformation
// Transform Reactflow data to the format that the server expects

export function transformFlowData(
  input: {
    nodes: Node<NodeDataBase<unknown>>[]
    edges: Edge[]
    global_meta?: {
      system_prompt: SystemPrompt | null
      stt_guidance?: string | null
      agent_name?: string | null
      agent_persona?: string | null
    }
  },
  flowType: FlowTypes,
  isPublish = false,
  isTransitioningFromDraft = false,
  // Playground saves pass true: keep existing node ids so the BE updates the
  // existing nodes in place instead of inserting a duplicate set on every run.
  forceStableIds = false,
) {
  const output: Omit<RFlowPayload, 'phlo'> = {
    connections: [],
    nodes: [],
    notes: {
      notes: [],
    },
    global_meta: input.global_meta || null,
  }

  const validationErrors: NodeValidationError[] = []
  const nodeMap: Record<string, string> = {}

  // Composite pre-pass: outbound_screening expands into its initiate_call +
  // contact_screening pair here, so every existing per-type path below (and
  // the edge loop) serializes plain builder nodes. Symmetric collapse lives
  // at the end of inverseTransformFlowData. The voicemail dedupe runs first,
  // while screening ports still sit on their builder-canvas node ids.
  const deduped = dedupeScreeningVoicemailEdges(input.nodes, input.edges)
  const expanded = expandOutboundScreeningComposites(deduped.nodes, deduped.edges)
  input = { ...input, nodes: expanded.nodes, edges: expanded.edges }

  try {
    // Process nodes
    // Changed is always true as without it BE wont make changes to the node
    // TODO: Remove this once BE is updated to handle changed flag
    const [notes, nodes] = partition(input.nodes, (node) => node.type === FlowNodeTypes.note)
    const shouldGenerateNewIds = isPublish && !isTransitioningFromDraft && !forceStableIds
    nodes.forEach((node) => {
      const id = v4()
      nodeMap[node.id] = shouldGenerateNewIds ? id : node.id

      const wireType = node.type

      try {
        const outputNode: ROutputNode = {
          id: shouldGenerateNewIds ? id : node.id,
          name: node.data.config.name,
          description: node.data.config.description ?? '',
          type: wireType as string,
          top: node.position.y,
          left: node.position.x,
          changed: true,
          component: wireType as FlowNodeTypes,
          last_editor_type: node.data.meta.last_editor_type,
          config: {
            // biome-ignore lint/suspicious/noExplicitAny: TODO - fix type definition
            model: getNodeConfig(node, flowType) as any,
            // Sibling of `model` on purpose: model keys are whitelist-projected by the
            // backend on GET; config-service re-injects this key like it does `_meta`.
            ...(node.data.name_source === 'user' ? { name_source: node.data.name_source } : {}),
          },
        }

        // Skip the menu node (it's not a real node)
        if (node.id !== MenuNodeID) output.nodes.push(outputNode)
      } catch (error) {
        if (error instanceof NodeValidationError) {
          validationErrors.push(error)
        } else {
          throw error // Re-throw non-validation errors
        }
      }
    })

    const flowStore = useFlowStore.getState()
    const hasGlobalMeta = 'global_meta' in input && input.global_meta !== undefined

    const systemPrompt = hasGlobalMeta
      ? (input.global_meta?.system_prompt ?? null)
      : flowStore.systemPrompt
    const sttGuidance = hasGlobalMeta
      ? (input.global_meta?.stt_guidance ?? null)
      : flowStore.sttGuidance
    const agentName = hasGlobalMeta ? (input.global_meta?.agent_name ?? null) : flowStore.agentName
    const agentPersona = hasGlobalMeta
      ? (input.global_meta?.agent_persona ?? null)
      : flowStore.agentPersona
    const systemPromptNodes = systemPrompt?.ai_nodes || []

    output.global_meta = systemPrompt?.prompt
      ? {
          system_prompt: {
            prompt: systemPrompt?.prompt || '',
            ai_nodes: shouldGenerateNewIds
              ? systemPromptNodes.map((node) => nodeMap[node])
              : systemPromptNodes,
            all_nodes_enabled: systemPrompt?.all_nodes_enabled,
          },
          stt_guidance: sttGuidance,
          agent_name: agentName,
          agent_persona: agentPersona,
        }
      : {
          system_prompt: null,
          stt_guidance: sttGuidance,
          agent_name: agentName,
          agent_persona: agentPersona,
        }
    // If there are validation errors, throw a collective error
    if (validationErrors.length > 0) {
      // Create a collective error with all node errors
      const collectiveError = new MultipleNodeValidationError(validationErrors)
      // Attach all individual errors to the collective error
      throw collectiveError
    }

    notes.forEach((node) => {
      const noteNode = node as Node<NoteNodeData>
      const outputNode = {
        id: node.id,
        title: noteNode.data.config.name,
        content: noteNode.data.config.content || '',
        color: noteNode.data.config.color || 'blue',
        x: noteNode.position.x,
        y: noteNode.position.y,
        width: node.width || 200,
        height: node.height || 100,
        zIndex: noteNode.zIndex || -1000,
      }
      if (output.notes?.notes) {
        output.notes.notes.push(outputNode)
      }
    })

    // Process edges to create connections
    const inboundCounts = new Map<string, number>()
    input.edges.forEach((edge) => {
      inboundCounts.set(edge.target, (inboundCounts.get(edge.target) ?? 0) + 1)
    })
    input.edges.forEach((edge) => {
      const sourceNodeId = nodeMap[edge.source] || edge.source
      const targetNodeId = nodeMap[edge.target] || edge.target
      const sourceNode = input.nodes.find((node) => node.id === edge.source)
      const targetNode = input.nodes.find((node) => node.id === edge.target)
      const sourceConfig = sourceNode?.data.config as Record<string, unknown> | undefined

      // For start node, preserve the edge's sourceHandle -- each Start
      // output port maps to a distinct trigger (message / call / http / ...).
      // Overwriting with FlowSourceTypes[flowType] produces ".undefined" for
      // omni flows (no 'omni' key in the map) and breaks port wiring for any
      // multi-channel flow. Fall back to the flow's primary channel only when
      // the handle is missing (legacy single-channel data with no port id).
      let sourceHandle = edge.sourceHandle || 'call'
      if (sourceNode?.type === FlowNodeTypes.start) {
        sourceHandle = edge.sourceHandle || FlowSourceTypes[flowType as FlowTypes] || 'call'
      }

      if (
        isAiAgentV2Type(sourceNode?.type) ||
        sourceNode?.type === FlowNodeTypes.agent_node ||
        sourceNode?.type === FlowNodeTypes.ai_action
      ) {
        const aiAgentNode = sourceNode as Node<AiAgentV2NodeData | AiActionNodeData>
        const intent = aiAgentNode.data.config.intents?.find((i) => i.id === sourceHandle)
        // fall back to id when name is missing to keep the edge valid
        sourceHandle = intent?.intent_name ?? sourceHandle
      }

      const targetOwner = targetNode?.data.meta.noReplyTargetOf
      const isOnlyLegacyNoReplyTarget =
        !targetOwner && targetNode?.deletable === false && inboundCounts.get(edge.target) === 1
      const isAutoCreatedSmsNoReplyTarget =
        isAiAgentV2Type(sourceNode?.type) &&
        sourceConfig?.channel === 'message' &&
        edge.sourceHandle === NO_REPLY_HANDLE &&
        targetNode?.type === FlowNodeTypes.agent_preset_conversation &&
        (targetOwner === edge.source || isOnlyLegacyNoReplyTarget)

      const connection = {
        data: {
          id: edge.id,
          // Config-service allows and preserves string connection types while
          // projecting unknown connection-data keys. Keep ownership in that
          // existing field so node models and backend schemas stay unchanged.
          type: isAutoCreatedSmsNoReplyTarget
            ? AUTO_CREATED_SMS_NO_REPLY_CONNECTION_TYPE
            : 'output',
        },
        source: `${sourceNodeId}.${sourceHandle}`,
        target: `${targetNodeId}.Input`,
      }

      // Skip the menu node (it's not a real connection)
      if (edge.id !== MenuNodeID) output.connections.push(connection)
    })
  } catch (e) {
    logger.error('Error in transformFlowData', e)
    // Re-throw validation errors so they can be handled by the caller
    throw e
  }

  return { data: output, nodeMap }
}

function extractData(input: DataItem[] | { data: DataItem[] }[]): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  input?.forEach((item) => {
    if (Array.isArray(item)) {
      item.forEach((subItem: { data: DataItem[] }) => {
        const subData = extractData([subItem])
        Object.assign(result, subData)
      })
    } else if (typeof item === 'object' && item !== null) {
      if ('data' in item) {
        const { data } = item

        if (Array.isArray(data)) {
          const subData = extractData(data)
          Object.assign(result, subData)
        } else if (typeof data === 'object' && data !== null) {
          Object.assign(result, data)
        } else if ('key' in item && item.key) {
          result[item.key] = data
        } else {
          result[(item as DataItem).label || (item as DataItem).type] = data
        }
      }
    }
  })
  return result
}

// Utility function to generate IDs for intents that don't have them
/** The builder-only keys the console's actionsSchema wants present on every AI node action. */
const generateActionIds = (data: Record<string, unknown>) => {
  const actions = data.actions as Array<Record<string, unknown>> | undefined
  if (!actions?.length) return data
  data.actions = actions.map((action) => ({
    action_name: null,
    action_instructions: null,
    ...action,
    id: (action.id as string) || v4(),
  }))
  return data
}

const generateIntentIds = (data: Record<string, unknown>) => {
  const nodeData = data as {
    intents?: Array<{
      id?: string
      intent_name?: string
      intent_instructions?: string
      intent_speech?: { speech_enabled?: boolean; intent_speech_text?: string }
    }>
  }
  if (nodeData.intents) {
    nodeData.intents = nodeData.intents.map((intent) => ({
      ...intent,
      id: intent.id || v4(), // Generate UUID if id doesn't exist
      intent_speech: intent.intent_speech || { speech_enabled: false, intent_speech_text: '' },
    }))
  }
  return nodeData
}

// Format config data BE - FE based on node type and output states
const formatConfigData = (
  data: Record<string, unknown>,
  nodeType: FlowNodeTypes,
  outputStates: OutputState[],
) => {
  switch (nodeType) {
    case FlowNodeTypes.agent_node: {
      const wire = (data.agent_tasks ?? {}) as AgentTasksWire
      return {
        ...data,
        agent_tasks: {
          variables: (wire.variables ?? []).map((v) => ({
            _uid: crypto.randomUUID(),
            name: v.name,
            instructions: v.instructions ?? '',
            type: v.type ?? 'text',
            params: v.params ?? {},
            optional: !!v.optional,
            ...(v.known_value !== undefined ? { known_value: v.known_value } : {}),
            ...(v.known_value !== undefined ? { confirm_if_known: !!v.confirm_if_known } : {}),
          })),
          extract_only: (wire.extract_only ?? []).map((v) => ({
            _uid: crypto.randomUUID(),
            name: v.name,
            instructions: v.instructions ?? '',
          })),
          ...(wire.confirm_announcement ? { confirm_announcement: wire.confirm_announcement } : {}),
          ...(wire.max_restarts != null ? { max_restarts: wire.max_restarts } : {}),
        },
      }
    }
    case FlowNodeTypes.start: {
      // Handle backward compatibility for quick_response_questions
      // If quick response related fields are not present, set them to default values
      const enableQuickResponses = data.enable_quick_responses || false
      const quickResponseQuestions = data.quick_response_questions || []
      const requireHttpAuth =
        data.require_http_auth === undefined || data.require_http_auth === null
          ? true
          : data.require_http_auth
      // Handle slack trigger fields, if they are null or undefined, set them to empty string
      const slackBotToken = !data.slack_bot_token ? '' : data.slack_bot_token
      const slackSigningSecret = !data.slack_signing_secret ? '' : data.slack_signing_secret
      const slackTriggerEvent = !data.slack_channel_id
        ? SlackChannelTypes.DIRECT
        : SlackChannelTypes.MENTION
      const slackReplyInThread = data.slack_channel_id ? false : data.slack_reply_in_thread

      return {
        ...data,
        enable_quick_responses: enableQuickResponses,
        quick_response_questions: quickResponseQuestions,
        require_http_auth: requireHttpAuth,
        slack_bot_token: slackBotToken,
        slack_signing_secret: slackSigningSecret,
        slack_channel_id: data.slack_channel_id,
        slack_trigger_event: slackTriggerEvent,
        slack_reply_in_thread: slackReplyInThread,
        // Node-level event callbacks (legacy PHLO dict -> builder array).
        events: eventsDictToArray(
          data.events as Record<string, { url: string; method: string }> | undefined,
        ),
      }
    }

    case FlowNodeTypes.stream: {
      // Reconstruct combined "provider:model" values for dropdown display
      const sttConfig = data.stt_config as Record<string, unknown> | undefined
      if (sttConfig?.provider && sttConfig?.model) {
        sttConfig.provider = `${sttConfig.provider}:${sttConfig.model}`
      }
      const ttsConfig = data.tts_config as Record<string, unknown> | undefined
      if (ttsConfig?.provider && ttsConfig?.model) {
        ttsConfig.provider = `${ttsConfig.provider}:${ttsConfig.model}`
      }
      return data
    }
    case FlowNodeTypes.ai_agent_v29:
    case FlowNodeTypes.ai_agent_v2: {
      const nodeData = generateActionIds(generateIntentIds(data) as Record<string, unknown>)
      // Remove wait_for_response and reply_timeout for call channel (not applicable)
      if (nodeData.channel === 'call') {
        delete nodeData.wait_for_response
        delete nodeData.reply_timeout
      }
      // the schema takes this boolean or nothing, and a stored null is neither
      if (nodeData.allow_greeting_interruption === null) delete nodeData.allow_greeting_interruption
      return nodeData
    }
    case FlowNodeTypes.contact_screening: {
      // Real wire component: builder fields are the wire fields minus channel.
      const wireModel = data as {
        context?: string
        ask_permission?: boolean
        initial_wait_time?: number
      }
      return {
        context: wireModel.context ?? '',
        ask_permission: wireModel.ask_permission ?? true,
        initial_wait_time: wireModel.initial_wait_time ?? DEFAULT_INITIAL_WAIT_TIME,
      }
    }
    case FlowNodeTypes.ai_action: {
      return generateIntentIds(data)
    }
    case FlowNodeTypes.agent_preset_conversation: {
      const presetData = data as { channel: 'whatsapp' | 'message' | 'chat' }

      // If channel is whatsapp, use the standardized WhatsApp data formatting utility
      if (presetData.channel === 'whatsapp') {
        return formatAiAgentWhatsappData(data)
      }

      // If channel is chat, use the standardized Chat data formatting utility
      if (presetData.channel === 'chat') {
        return formatAiAgentChatData(data)
      }

      // If channel is message, apply message-specific formatting
      if (presetData.channel === 'message') {
        const messageData = data as {
          disable_inequipped_to_serve?: boolean
          events?: Record<string, { url: string; method: string }>
        }
        const disableInequippedToServe = messageData.disable_inequipped_to_serve ?? false
        return {
          ...data,
          disable_inequipped_to_serve: disableInequippedToServe,
          events: eventsDictToArray(messageData.events),
        }
      }

      // For any other channels, return data as-is
      return data
    }

    case FlowNodeTypes.ai_agent_call: {
      const disableInequippedToServe = data.disable_inequipped_to_serve ?? false
      return { ...data, disable_inequipped_to_serve: disableInequippedToServe }
    }
    case FlowNodeTypes.http_request: {
      const httpRequest = data.http_request as {
        body_type: string
        body: string
        name?: string
        auth_params?: string
      }
      const body = JSON.stringify({
        ...JSON.parse(initialBody),
        [httpRequest.body_type]: JSON.parse(httpRequest.body),
      })
      const httpRequestData = {
        http_request:
          data.http_request && 'name' in data
            ? {
                ...(data.http_request as object),
                name: data.name,
                auth_params:
                  typeof data.http_request === 'object' &&
                  data.http_request !== null &&
                  'auth_params' in data.http_request
                    ? JSON.parse(data.http_request.auth_params as string)
                    : {
                        username: '',
                        password: '',
                        token: '',
                      },
                body,
              }
            : data.http_request,
      }
      return httpRequestData
    }
    case FlowNodeTypes.business_hour: {
      const businessHour = outputStates
        .filter((state) => !state.default)
        .map((state) => ({
          uuid: state.uuid,
          id: state.name,
          name: state.name,
          timezone: state.timezone,
          default: state.default,
        }))
      const result = { ...data, business_hrs: businessHour }
      return result
    }
    case FlowNodeTypes.voicemail: {
      // Node-level event callbacks round-trip as a dict on the BE; the builder
      // (and the save transform) expect an array. Convert on load so a saved
      // { record: { url, method } } is not silently dropped on the next save.
      // SER-5972.
      const events =
        'events' in data
          ? eventsDictToArray(
              data.events as Record<string, { url: string; method: string }> | undefined,
            )
          : undefined
      if ('transcription_type' in data) {
        const { transcription_type, transcription_url } = data.transcription_type as Record<
          string,
          unknown
        >
        return {
          ...data,
          ...(events ? { events } : {}),
          transcription_type,
          transcription_url,
        }
      }
      return events ? { ...data, events } : data
    }
    case FlowNodeTypes.create_task: {
      const taskData = data as { prompt: typeof promptData }
      if (!taskData.prompt.text.trim()) {
        taskData.prompt = promptData
      }
      return taskData
    }
    case FlowNodeTypes.send_chat: {
      const sendChatData = data as {
        chat_content_type: 'text' | 'chat_media'
        text: string
        chat_media: Record<string, never>
      }
      if (sendChatData.chat_content_type === 'text') sendChatData.chat_media = {}
      else sendChatData.text = ''
      return sendChatData
    }
    case FlowNodeTypes.ai_agent_chat: {
      return formatAiAgentChatData(data)
    }
    case FlowNodeTypes.send_whatsapp: {
      const defaultSendWhatsappConfig: {
        template: unknown
        whatsapp_template: unknown
        variables: {
          header: string[]
          body: string[]
        }
      } = {
        template: {},
        whatsapp_template: {},
        variables: {
          header: [],
          body: [],
        },
      }
      const whatsappData = data as {
        whatsapp_msg_type: 'template' | 'custom'
        whatsapp_template: {
          template_uuid: string
          name?: string
          namespace?: string
          language?: { code: string; policy?: string }
          components?: Array<{
            type: string
            parameters?: Array<{
              text?: string
              type?: string
              image?: { link?: string }
              video?: { link?: string }
              document?: { link?: string }
            }>
          }>
        }
      }
      if (whatsappData.whatsapp_template?.template_uuid) {
        // Try to get full template from cache (backend might not have loaded templates yet)
        const cachedTemplate = getTemplate(whatsappData.whatsapp_template?.template_uuid)

        // Extract variables from whatsapp_template components using shared helper
        const extractedVariables = extractTemplateVariables(
          whatsappData.whatsapp_template?.components,
        )
        defaultSendWhatsappConfig.variables = extractedVariables

        // Use cached template if available, otherwise reconstruct minimal template from backend data
        const templateToUse = cachedTemplate || {
          uuid: whatsappData.whatsapp_template.template_uuid,
          data: {
            name: whatsappData.whatsapp_template.name || '',
            language: whatsappData.whatsapp_template.language?.code || '',
            namespace: whatsappData.whatsapp_template.namespace || '',
            components: whatsappData.whatsapp_template.components || [],
            status: 'approved',
          },
        }

        const returnData = {
          ...defaultSendWhatsappConfig,
          ...data,
          template: templateToUse,
        }
        return returnData
      }

      const returnData = {
        ...data,
        ...defaultSendWhatsappConfig,
      }
      return returnData
    }
    case FlowNodeTypes.ai_agent_whatsapp: {
      return formatAiAgentWhatsappData(data)
    }
    case FlowNodeTypes.ai_agent_message: {
      const disableInequippedToServe = data.disable_inequipped_to_serve ?? false
      return { ...data, disable_inequipped_to_serve: disableInequippedToServe }
    }
    case FlowNodeTypes.queue_and_route: {
      const queueCallAllowedInputs = outputStates
        .filter((state) => !state.default && state.selected)
        .map((state) => state.id)
      const routingAgents = (data.routing_agents as string[]) || []
      // Split routing_agents back into agents and variables
      // Variables are identified by the {{...}} pattern
      const routingVariables: string[] = []
      const actualRoutingAgents: string[] = []
      routingAgents.forEach((item) => {
        if (typeof item === 'string' && item.startsWith('{{') && item.endsWith('}}')) {
          routingVariables.push(item)
        } else {
          actualRoutingAgents.push(item)
        }
      })
      const result: Record<string, unknown> = {
        ...data,
        routing_agents: actualRoutingAgents,
        routing_variables: routingVariables,
        queue_call_allowed_inputs: queueCallAllowedInputs,
      }
      return result
    }
    case FlowNodeTypes.initiate_call: {
      const result: Record<string, unknown> = {
        ...data,
        events: eventsDictToArray(
          data.events as Record<string, { url: string; method: string }> | undefined,
        ),
      }
      if (result.max_call_duration && typeof result.max_call_duration === 'number') {
        result.max_call_duration = Math.round((result.max_call_duration as number) / 60) // seconds -> minutes
      }
      return result
    }
    case FlowNodeTypes.multi_party_call_v2:
    case FlowNodeTypes.conference_bridge: {
      const { events, record, recording, ...rest } = data as Record<string, unknown>
      const result: Record<string, unknown> = {
        ...rest,
        events: eventsDictToArray(
          events as Record<string, { url: string; method: string }> | undefined,
        ),
      }
      // phlo-core's GET meta nests the flat record/record_value pair into
      // { record: { record, record_value } } for these two components
      // (AS_DICTIONARY rendering group). Flatten back to the builder fields —
      // the truthy object otherwise reads as Recording ON and re-saving it
      // fails BE validation. A non-empty record_value means dynamic recording.
      // The wire key for the recording option is `recording`. SER-5919.
      if (isPlainObject(record)) {
        const recordValue = typeof record.record_value === 'string' ? record.record_value : ''
        if (recordValue.trim()) {
          result.record = true
          result.record_mode = 'dynamic'
          result.record_condition = recordValue
        } else {
          result.record = record.record === true
          result.record_mode = 'always'
          result.record_condition = ''
        }
      } else if (record !== undefined) {
        result.record = record
      }
      if (
        nodeType === FlowNodeTypes.multi_party_call_v2 &&
        typeof recording === 'string' &&
        recording
      ) {
        result.recording_option = recording
      }
      return result
    }
    case FlowNodeTypes.send_message:
    case FlowNodeTypes.get_input:
    case FlowNodeTypes.ivr_menu:
    case FlowNodeTypes.call_forward: {
      const { events, ...rest } = data as Record<string, unknown>
      return {
        ...rest,
        events: eventsDictToArray(
          events as Record<string, { url: string; method: string }> | undefined,
        ),
      }
    }
    default:
      return data
  }
}
// Legacy/duplicated flows store many typed fields as null (or omit them). The
// builder form schemas require concrete types, and the load path below uses the
// saved config verbatim — it does NOT apply node defaults. So opening + saving
// such a node fails with "Expected array/boolean/number, received null". Fill
// null/absent fields from the node's own default config (defaultNodeData) on
// load — a present value (incl. [], false, 0, '') always wins; only null/missing
// gets the default. Scoped to the legacy call/SMS node types that come from PHLO
// duplication; AI nodes are untouched. SER-4772.
const LEGACY_DEFAULT_FILL_TYPES = new Set<FlowNodeTypes>([
  FlowNodeTypes.send_message,
  FlowNodeTypes.voicemail,
  FlowNodeTypes.initiate_call,
  FlowNodeTypes.get_input,
  FlowNodeTypes.ivr_menu,
  FlowNodeTypes.call_forward,
  FlowNodeTypes.conference_bridge,
  FlowNodeTypes.multi_party_call_v2,
])

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v)

const CONFIRM_KEY_WORDS: Record<string, string> = { '#': 'the pound key', '*': 'the star key' }
export const confirmPromptFor = (key: unknown) =>
  `You have an incoming forwarded call. Press ${CONFIRM_KEY_WORDS[String(key ?? '#')] ?? String(key)} to accept.`
export const isGeneratedConfirmPrompt = (text: string) =>
  CONFIRM_KEYS.some((key) => confirmPromptFor(key) === text)

export const fillNullDefaults = (loaded: unknown, defaults: unknown): unknown => {
  if (loaded == null) return defaults
  if (isPlainObject(loaded) && isPlainObject(defaults)) {
    const out: Record<string, unknown> = { ...loaded }
    for (const key of Object.keys(defaults)) {
      out[key] = fillNullDefaults(loaded[key], defaults[key])
    }
    return out
  }
  return loaded
}

// BE Server -> FE Reactflow transformation
// Transform the server response to the format that Reactflow expects
export function inverseTransformFlowData(input: RFlowPayload): FlowData {
  try {
    const rawNodes = input.nodes

    // If the flow has no nodes, add the default start node to the flow
    if (rawNodes.length === 0) {
      const defaultNewFlowData = getNewFlowData()
      rawNodes.push(...defaultNewFlowData.nodes)
    }
    const nodes: Node<NodeDataBase<unknown>>[] = input.nodes.map((node) => {
      const data = extractData(node.config.model) as NodeDataBase<unknown>['config']
      const nodeType = node.type as FlowNodeTypes
      const formattedData = {
        name: data.name as string,
        description: (node.description ?? '') as string,
        ...formatConfigData(data, nodeType, node.config.output_states as OutputState[]),
      }
      const componentDefaultData = defaultNodeData[nodeType]
      const config =
        LEGACY_DEFAULT_FILL_TYPES.has(nodeType) && componentDefaultData?.config
          ? (fillNullDefaults(formattedData, componentDefaultData.config) as typeof formattedData)
          : formattedData
      return {
        id: node.id,
        type: nodeType,
        data: {
          meta: { name: componentDefaultData?.meta?.name },
          // TEMPORARY WORKAROUND (v3 edit-lock; remove when the task compiler is re-enabled):
          // carry the server-derived compiled task graph through, read-only, so the
          // console can gate edit/publish on v3-task-runtime flows. Kept outside
          // `config` so transformNodeData never sends it back on save.
          _meta: node.config._meta ?? null,
          ...(node.config.name_source === 'user' ? { name_source: node.config.name_source } : {}),
          config,
        },
        position: { x: node.left, y: node.top },
        zIndex: 1000,
      }
    })

    nodes.push(
      ...(input.notes?.notes?.map((note) => ({
        id: note.id,
        type: FlowNodeTypes.note,
        data: {
          meta: { name: note.title },
          config: {
            name: `Note ${note.id}`,
            content: note.content,
            color: note.color,
          },
        },
        zIndex: note.zIndex,
        width: note.width,
        height: note.height,
        position: { x: note.x, y: note.y },
      })) || []),
    )

    const nodesLookup = keyBy(nodes, 'id')

    const edges = input.connections?.map((connection) => {
      const [sourceNodeId, sourceHandle] = connection.source.split('.') // Extract both ID and handle
      const targetNodeId = connection.target.split('.')[0] // Extract node ID
      const sourceNode = nodesLookup[sourceNodeId]
      const targetNode = nodesLookup[targetNodeId]
      const sourceConfig = sourceNode?.data.config as Record<string, unknown> | undefined

      // Restore UI-only ownership only when the saved marker still agrees with
      // every structural part of the SMS timeout branch.
      if (
        connection.data.type === AUTO_CREATED_SMS_NO_REPLY_CONNECTION_TYPE &&
        isAiAgentV2Type(sourceNode?.type) &&
        sourceConfig?.channel === 'message' &&
        sourceHandle === NO_REPLY_HANDLE &&
        targetNode?.type === FlowNodeTypes.agent_preset_conversation
      ) {
        targetNode.data.meta = {
          ...targetNode.data.meta,
          noReplyTargetOf: sourceNodeId,
        }
      }

      let newSourceHandle = sourceHandle
      if (
        (isAiAgentV2Type(sourceNode?.type) ||
          sourceNode?.type === FlowNodeTypes.agent_node ||
          sourceNode?.type === FlowNodeTypes.ai_action) &&
        // "no reply" is the chat AI node's reply-timeout port, not an intent —
        // it must stay verbatim or the saved timeout branch detaches on load.
        sourceHandle !== NO_REPLY_HANDLE
      ) {
        const aiAgentNode = sourceNode as Node<AiAgentV2NodeData | AiActionNodeData>
        const intent = aiAgentNode.data.config.intents?.find((i) => i.intent_name === sourceHandle)
        newSourceHandle = intent?.id || ''
      }

      return {
        id: connection.data.id,
        source: sourceNodeId,
        target: targetNodeId,
        sourceHandle: newSourceHandle,
        data: {
          // Feature: Nodevars
          nodeVars: getNodeVarsForHandle(sourceNode, newSourceHandle),
        },
      }
    })

    // Composite post-pass: fold initiate_call → answered → contact_screening
    // pairs back into one outbound_screening builder node. Both halves are
    // already builder-formatted at this point.
    const folded = collapseOutboundScreeningPairs(nodes, edges ?? [])

    // Create the base FlowData object
    const flowData: FlowData = {
      nodes: folded.nodes,
      edges: folded.edges as FlowData['edges'],
      phlo: input.phlo,
      global_meta: input.global_meta,
    }

    // Process validation errors from the response if they exist
    if (input.validation_errors) {
      const validationErrors = processValidationErrors(
        { response: { data: { data: input.validation_errors } } },
        flowData.nodes,
      )

      // Add validation errors to FlowData
      flowData.validation_errors = validationErrors

      // Apply validation errors to nodes for highlighting
      const backendValidationIssues = handleBackendValidationErrors(
        { response: { data: { data: input.validation_errors } } },
        input,
      )

      // Group issues by node ID to avoid overwriting
      const issuesByNodeId = new Map<string, ZodCustomIssue[]>()
      backendValidationIssues.forEach((issue) => {
        const nodeId = issue.params?.data?.id
        if (nodeId) {
          if (!issuesByNodeId.has(nodeId)) {
            issuesByNodeId.set(nodeId, [])
          }
          issuesByNodeId.get(nodeId)?.push(issue)
        }
      })

      // Update each node with all its issues at once. Screening halves fold
      // into their composite on load, so their wire-id-keyed issues must
      // remap onto the composite node or they never highlight.
      const screeningWireIds = buildScreeningWireIdMap(flowData.nodes)
      issuesByNodeId.forEach((issues, nodeId) => {
        const canvasId = screeningWireIds.get(nodeId) ?? nodeId
        const existingNode = flowData.nodes.find((n) => n.id === canvasId) as GenericNode
        if (existingNode) {
          // Update node with all validation errors for highlighting
          existingNode.data.meta.issues = issues
        }
      })
    } else {
      // Clear validation errors if none in input
      flowData.validation_errors = []
    }

    return flowData
  } catch (e) {
    logger.error('Error in inverseTransformFlowData', e)
    return {
      nodes: [],
      edges: [],
      phlo: { name: '', group: '', enabled: false, state: AgentState.DRAFT },
    }
  }
}
